import type { CiselnikyVolbyDto, PodaniChybyDto } from '@gov-nx/api';
import { sendSubmissionQuery, useAxiosInstance, usePoQuery } from '@gov-nx/api';
import { useLocale } from '@gov-nx/core/service';
import type {
	IElectionControl,
	VotersCardFormData,
} from '@gov-nx/module/service';
import { ServiceCode, transformControl } from '@gov-nx/module/service';
import { prepareCheckData } from './formDefinitions';

interface Props {
	election?: CiselnikyVolbyDto;
	electionId: number;
	rounds: VotersCardFormData['electionRound'];
}

export interface InitialCheckResult {
	isLoading: boolean;
	initialErrors: IElectionControl['errors'];
}

export const useInitialCheck = ({
	election,
	electionId,
	rounds,
}: Props): InitialCheckResult => {
	const { t } = useLocale(ServiceCode['volicsky-prukaz']);
	const { axiosInstance } = useAxiosInstance();

	const canBeSubmitted = !!election?.kola?.find(
		(round) => round.lzePodatZadost
	);
	const allowedCodes = ['NesplnenaPodminkaVek', 'nelze'];

	const query = usePoQuery<InitialCheckResult['initialErrors']>({
		queryKey: ['initialCheck'],
		queryFn: async () => {
			if (!canBeSubmitted) {
				return [{ code: 'nelze', message: t('volby.error.nelze-podat.text') }];
			}

			const prepared = prepareCheckData(rounds, electionId, election);
			const response = (await sendSubmissionQuery(axiosInstance)(
				prepared
			)) as unknown as PodaniChybyDto;

			return transformControl(response, t).errors;
		},
		enabled: !!election,
	});

	return {
		isLoading: query.isLoading,
		initialErrors:
			query.data?.filter(({ code }) => allowedCodes.includes(code)) ?? [],
	};
};
